import React from "react";

import { Line, Rect, Text } from "react-konva";
import { useTheme } from "@mui/material";

import { RULER_HEIGHT_IN_PX } from "../../constants";
import { formatMillisecondsToHHMMSSForRuler } from "../../../../../utils/formatMillisecondsToHHMMSSForRuler";

interface RulerProps {
  totalMilliseconds: number;
  pixelsPerMillisecond: number;
}

const MINOR_TICKS_PER_STEP = 5;

export default function Ruler({
  totalMilliseconds,
  pixelsPerMillisecond,
}: RulerProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const labelColor = isDark ? "#8b8fa3" : "#5f6368";
  const majorTickColor = isDark ? "#4a4d5e" : "#b0b4bc";
  const minorTickColor = isDark ? "#33364a" : "#d6d9de";
  const baselineColor = isDark ? "#2a2b3d" : "#e0e0e0";

  const majorStepIntervals = [
    1000, 5000, 15000, 30000, 60000, 300000, 600000, 900000, 1800000, 3600000,
  ];

  // Find an appropriate major step size based on the zoom level (pixelsPerMillisecond)
  const majorStepSize =
    majorStepIntervals.find(
      (interval) => interval * pixelsPerMillisecond >= 50, // Ensure at least 50 pixels between major steps
    ) || majorStepIntervals[majorStepIntervals.length - 1];

  const totalWidth = totalMilliseconds * pixelsPerMillisecond;
  const ticks: React.ReactNode[] = [];

  for (let ms = 0; ms <= totalMilliseconds; ms += majorStepSize) {
    const x = ms * pixelsPerMillisecond;
    ticks.push(
      <React.Fragment key={ms}>
        <Line
          points={[x, RULER_HEIGHT_IN_PX - 12, x, RULER_HEIGHT_IN_PX]}
          stroke={majorTickColor}
          strokeWidth={1}
          listening={false}
        />
        <Text
          x={x + 4}
          y={RULER_HEIGHT_IN_PX - 24}
          text={formatMillisecondsToHHMMSSForRuler(ms)}
          fontSize={11}
          fontStyle="600"
          fill={labelColor}
          listening={false}
        />
      </React.Fragment>,
    );
    for (let m = 1; m < MINOR_TICKS_PER_STEP; m += 1) {
      const minorX =
        x + (m * majorStepSize * pixelsPerMillisecond) / MINOR_TICKS_PER_STEP;
      if (minorX <= totalWidth) {
        ticks.push(
          <Line
            key={`${ms}-${m}`}
            points={[
              minorX,
              RULER_HEIGHT_IN_PX - 5,
              minorX,
              RULER_HEIGHT_IN_PX,
            ]}
            stroke={minorTickColor}
            strokeWidth={1}
            listening={false}
          />,
        );
      }
    }
  }

  return (
    <>
      <Rect
        width={totalWidth}
        height={RULER_HEIGHT_IN_PX}
        fill={theme.palette.background.paper}
        opacity={isDark ? 0.6 : 1}
        listening={false}
      />
      <Line
        points={[0, RULER_HEIGHT_IN_PX, totalWidth, RULER_HEIGHT_IN_PX]}
        stroke={baselineColor}
        listening={false}
      />
      {ticks}
    </>
  );
}
